import { reachabilityMonitor } from '@alucio/core';
import { CRMIntegration, Tenant } from '@alucio/aws-beacon-amplify/src/models'
import { observeSlice } from 'src/state/redux'
import { SliceState } from 'src/state/redux/slice/common'
import { Hub, HubCapsule } from '@aws-amplify/core'

import { Observable } from 'rxjs';
import { islastSyncLessThan24Hours } from './crmMachine';
import { REFRESH_DATA } from './crmMachineTypes';
import { CRM_LAST_SYNC_AT, CRMUtil } from './util';

export type CRMAutoSync = Observable<REFRESH_DATA>
export type CRMMessageChannel = Observable<{ type: 'LOGOUT_MESSAGE', payload: { message?: undefined | string } }>
export type CRMKeepVaultSessionAlive = Observable<{ type: 'KEEP_SESSION_ALIVE' }>

export const observableTenant = () => {
  return observeSlice<SliceState<Tenant>>(
    state => state.tenant,
    undefined,
  )
    .filter(tenants => {
      return tenants.records.length > 0 && !!tenants.records[0].config.crmIntegration
    })
    .map<{ type: 'SET_CONFIG' | 'RESET_CONFIG', payload: CRMIntegration | undefined }>(tenants => {
      const crmIntegration = tenants.records[0]?.config.crmIntegration
      return {
        type: crmIntegration ? 'SET_CONFIG' : 'RESET_CONFIG',
        payload: crmIntegration,
      }
    })
}

export const observableAutoSync = (): CRMAutoSync => {
  let userIsOnline = true
  reachabilityMonitor.subscribe( e => {
    userIsOnline = e.online
  })

  return new Observable(observer => {
    const interval = setInterval(() => {
      if (navigator.onLine === false || userIsOnline === false) {
        return
      }

      const lastSyncAt = localStorage.getItem(CRM_LAST_SYNC_AT)
      const isUserInAMeeting = window.location.pathname.startsWith('/meeting-presentation')
      if (lastSyncAt && !isUserInAMeeting) {
        const shouldAutoSync = !islastSyncLessThan24Hours(new Date(lastSyncAt))
        if (shouldAutoSync) {
          observer.next({
            type: 'REFRESH_DATA',
            payload: { trigger: 'SYSTEM' },
          })
        }
      }
    }, 60 * 60 * 1000)

    return () => clearInterval(interval);
  })
}

export const observableLogOutMessage = (): CRMMessageChannel => {
  return new Observable(observer => {
    Hub.listen('auth', (data: HubCapsule<'auth', any>) => {
      if (data.payload.event === 'signedOut') {
        observer.next({
          type: 'LOGOUT_MESSAGE',
          payload: { message: 'You have been logged out' },
        })
      }
    },
    )
  })
}

export const observableKeepSessionAlive = (): CRMKeepVaultSessionAlive => {
  const authInformation = CRMUtil.getAuthInformation();
  const intervalValueInSeconds = authInformation?.maximumInactiveSessionDurationInSeconds || 1200;
  const sessionAgeExceedsInterval = authInformation?.sessionIdIssuanceDatetime
    ? (Date.now() - new Date(authInformation.sessionIdIssuanceDatetime).getTime()) / 1000 > intervalValueInSeconds
    : false;

  // REFRESH THE SESSION 2 MINUTES BEFORE IT SHOULD EXPIRE (9MIN BEING THE MINIMUM AMOUNT) //
  const keepSessionAliveInterval = Math.max((intervalValueInSeconds - 120) * 1000, 9 * 60 * 1000);

  return new Observable(observer => {
    let timeout: ReturnType<typeof setTimeout> | undefined

    if (sessionAgeExceedsInterval) {
      timeout = setTimeout(() => observer.next({ type: 'KEEP_SESSION_ALIVE' }), 1000);
    }

    const interval = setInterval(() => {
      observer.next({ type: 'KEEP_SESSION_ALIVE' })
    }, keepSessionAliveInterval);

    return () => {
      clearTimeout(timeout)
      clearInterval(interval)
    };
  })
}
